import {faFile, faFilePdf, faFileExcel} from "@fortawesome/free-regular-svg-icons"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {useTranslation} from "react-i18next"
import {createUseStyles} from "react-jss"
import {apiConfig} from "../apiConfig"
import {Colors} from "../constants/colors"
import {Fonts} from "../constants/fonts"
import {EmployerInfoModel, FileModel, FileType, Language} from "../redux/types"
import AuthenticatedFileLink from "./AuthenticatedFileLink"
import InfoIcon from "./InfoIcon"
import {useAppSelector} from "../redux/hooks";
import WarningIcon from "./WarningIcon"

interface Props {
    file: FileModel,
    isCompanyGroupFile?: boolean
    expectedIncludedEmployerCount?: number | null
}

const File = ({ file, isCompanyGroupFile = false, expectedIncludedEmployerCount = null }:Props) => {
    const styles = useStyles()
    const { t } = useTranslation(['files', 'employees']);
    const language = useAppSelector(state => state.auth.authenticatedUser?.language) ?? Language.en

    const getFileIcon = (file: FileModel) => {
        switch (file.type) {
            case FileType.payDeductionNotificationFile:
            case FileType.creditReportFile:
                return faFilePdf;
            
            case FileType.agiAdjustmentReportFile:
            case FileType.foraAdjustmentReportFile:
            case FileType.remainingDebtReportFile:
            case FileType.employeeForeclosureReportFile:
            case FileType.grossAdvanceReportFile:
            case FileType.manualSendBackPaysReportFile:
                return faFileExcel;
            
            case FileType.bankAccountFile:
            case FileType.agiAdjustmentSieFile:
            case FileType.employeeDeactivationFile:
            default:
                return faFile;
        }
    }

    const renderFile = () => {
        return (
            <div className={styles.fileContainer} key={`file_${file.id}`}>
                <FontAwesomeIcon
                    icon={getFileIcon(file)}
                    style={{marginTop: 0}}
                    color={Colors.gray1}
                />
                { renderFileLink(file, isCompanyGroupFile) }
                <InfoIcon text={getFileDescription(file) ?? ''}/>
                {isCompanyGroupFile && expectedIncludedEmployerCount != null &&
                    renderPossibleMissingEmployerWarning(file, expectedIncludedEmployerCount)
                }
            </div>
        )
    }

    const renderPossibleMissingEmployerWarning = (file: FileModel, expectedIncludedEmployerCount : number) => {
        let includedEmployers : EmployerInfoModel[] = []

        if (file.type === FileType.advancePayoutBasisFile) {
            includedEmployers = file.advancePayoutBasisFile.includedEmployers
        } else if (file.type == FileType.deductionPayoutBasisFile) {
            includedEmployers = file.deductionPayoutBasisFile.includedEmployers
        } else if (file.type == FileType.bankAccountFile) {
            includedEmployers = file.bankAccountFile.includedEmployers
        } else if (file.type == FileType.employeeDeactivationFile) {
            includedEmployers = file.employeeDeactivationFile.includedEmployers
        }

        if (includedEmployers.length != expectedIncludedEmployerCount) {
            let includedEmployerNames = includedEmployers.map(x => x.name).join(", ")
            let warningText = t('unexpectedIncludedEmployerCountWarningTemplate', {ns: 'files', employerNames: includedEmployerNames})
            return (
                <WarningIcon text={warningText} />
            )
        }
    }

    const renderFileLink = (file: FileModel, isCompanyGroupFile: boolean) => {
        let baseUrl;
        let url;
        let className;

        switch (file.type) {
            case FileType.payDeductionNotificationFile:
                url = apiConfig.routes.paymentNotificationFiles + file.id
                className = file.employerPayDeductionPaymentNotificationFile?.lastDownloadDate == null ? styles.newFile : styles.readFile
                break
            case FileType.advancePayoutBasisFile:
                baseUrl = isCompanyGroupFile ? apiConfig.routes.companyGroupAdvancePayoutBasisFile : apiConfig.routes.advancePayoutBasisFile
                url = baseUrl + `${file.advancePayoutBasisFile.year}/${file.advancePayoutBasisFile.month}`
                className = styles.readFile  // Always treat as read/not bold
                break
            case FileType.deductionPayoutBasisFile:
                baseUrl = isCompanyGroupFile ? apiConfig.routes.companyGroupDeductionPayoutBasisFile : apiConfig.routes.deductionPayoutBasisFile
                url = baseUrl + file.deductionPayoutBasisFile.paydayPaymentDate
                className = styles.readFile  // Always treat as read/not bold
                break
            case FileType.bankAccountFile:
                baseUrl = isCompanyGroupFile ? apiConfig.routes.companyGroupBankAccountImportFile : apiConfig.routes.bankAccountImportFile
                url = baseUrl
                className = styles.readFile  // Always treat as read/not bold
                break
            case FileType.employeeDeactivationFile:
                baseUrl = isCompanyGroupFile ? apiConfig.routes.companyGroupEmployeeDeactivationImportFile : apiConfig.routes.employeeDeactivationImportFile
                url = baseUrl
                className = styles.readFile  // Always treat as read/not bold
                break
            case FileType.creditReportFile:
                url = apiConfig.routes.creditReportFiles + file.id
                className = file.creditReportFile?.lastDownloadDate == null ? styles.newFile : styles.readFile
                break
            case FileType.agiAdjustmentSieFile:
                url = apiConfig.routes.agiAdjustmentSieFiles + file.id
                className = file.agiAdjustmentSieFile?.lastDownloadDate == null ? styles.newFile : styles.readFile
                break
            case FileType.agiAdjustmentReportFile:
                url = apiConfig.routes.agiAdjustmentReportFile + `?period=${file.agiAdjustmentReportFile.period}&language=${language}`
                className = styles.readFile
                break
            case FileType.foraAdjustmentReportFile:
                url = apiConfig.routes.foraAdjustmentReportFile + `?period=${file.foraAdjustmentReportFile.period}&language=${language}`
                className = styles.readFile
                break
            case FileType.remainingDebtReportFile:
                url = apiConfig.routes.remainingDebtReportFile + `?paydayId=${file.remainingDebtReportFile.paydayId}&language=${language}`
                className = styles.readFile
                break
            case FileType.employeeForeclosureReportFile:
                url = apiConfig.routes.employeeForeclosureReportFile + `?paydayId=${file.employeeForeclosureReportFile.paydayId}&language=${language}`
                className = styles.readFile
                break
            case FileType.grossAdvanceReportFile:
                url = apiConfig.routes.grossAdvanceReportFile + `?year=${file.grossAdvanceReportFile.year}&month=${file.grossAdvanceReportFile.month}&language=${language}`
                className = styles.readFile
                break
            case FileType.manualSendBackPaysReportFile:
                url = apiConfig.routes.manualSendBackPaysReportFile + `?paydayId=${file.manualSendBackPaysReportFile.paydayId}&language=${language}`
                className = styles.readFile
                break
        }

        return (
            <AuthenticatedFileLink fileId={file.id} url={url} filename={getFilename(file)}>
                <span style={{flexWrap: 'nowrap', fontSize: 13}} className={className}>{getFileTitle(file)}</span>
            </AuthenticatedFileLink>
        )
    }

    const getFileTitle = (file: FileModel) => {
        switch (file.type) {
            case FileType.payDeductionNotificationFile:
                return t(`payDeductionPaymentNotificationFilename`, {ns: 'files', payday: file.employerPayDeductionPaymentNotificationFile.paydayPaymentDate})
            case FileType.advancePayoutBasisFile:
                return t(`calendarMonthPayoutsFilename`, {ns: 'files', month: `${file.advancePayoutBasisFile.year}-${padWithLeadingZeroes(file.advancePayoutBasisFile.month, 2)}`})
            case FileType.deductionPayoutBasisFile:
                return t(`paydayDeductionFilename`, {ns: 'files', payday: file.deductionPayoutBasisFile.paydayPaymentDate})
            case FileType.bankAccountFile:
                return t(`bankAccountsFilename`, {ns: 'files'})
            case FileType.employeeDeactivationFile:
                return t(`employeeDeactivationFilename`, {ns: 'files'})
            case FileType.creditReportFile:
                return t(`creditReportFilename`, {ns: 'files'})
            case FileType.agiAdjustmentSieFile:
                return t(`agiAdjustmentSieFilename`, {ns: 'files'})
            case FileType.agiAdjustmentReportFile:
                return t(`agiAdjustmentReportFilename`, {ns: 'files'})
            case FileType.foraAdjustmentReportFile:
                return t(`foraAdjustmentReportFilename`, {ns: 'files'})
            case FileType.remainingDebtReportFile: {
                const title = t(`remainingDebtReportFilename`, {ns: 'files'})
                const paydayMonth = file.remainingDebtReportFile.payday.substring(0, 7)
                return `${title} - ${paydayMonth}`
            }
            case FileType.employeeForeclosureReportFile:
                return t(`employeeForeclosureReportFilename`, {ns: 'files'})
            case FileType.grossAdvanceReportFile:
                return t(`grossAdvanceReportFilename`, {ns: 'files'})
            case FileType.manualSendBackPaysReportFile:
                return t(`manualSendBackPaysReportFile`, {ns: 'files'})
        }
    }

    const getFileDescription = (file: FileModel) => {
        switch (file.type) {
            case FileType.payDeductionNotificationFile:
                return t(`payDeductionPaymentNotificationDescription`, {ns: 'files', payday: file.employerPayDeductionPaymentNotificationFile.paydayPaymentDate})
            case FileType.advancePayoutBasisFile:
                return t(`calendarMonthPayoutsDescription`, {ns: 'files', month: `${file.advancePayoutBasisFile.year}-${padWithLeadingZeroes(file.advancePayoutBasisFile.month, 2)}`})
            case FileType.deductionPayoutBasisFile:
                return t(`paydayDeductionDescription`, {ns: 'files', payday: file.deductionPayoutBasisFile.paydayPaymentDate})
            case FileType.bankAccountFile:
                return t(`bankAccountsDescription`, {ns: 'files'})
            case FileType.employeeDeactivationFile:
                return t(`employeeDeactivationDescription`, {ns: 'files'})
            case FileType.creditReportFile:
                return t(`creditReportDescription`, {ns: 'files'})
            case FileType.agiAdjustmentSieFile:
                return t(`agiAdjustmentSieFileDescription`, {ns: 'files'})
            case FileType.agiAdjustmentReportFile:
                return t(`agiAdjustmentReportFileDescription`, {ns: 'files'})
            case FileType.foraAdjustmentReportFile:
                return t(`foraAdjustmentReportFileDescription`, {ns: 'files'})
            case FileType.remainingDebtReportFile:
                return t(`remainingDebtReportFileDescriptionTemplate`, {ns: 'files', payday: file.remainingDebtReportFile.payday})
            case FileType.employeeForeclosureReportFile:
                return t(`employeeForeclosureReportFileDescriptionTemplate`, {ns: 'files', payday: file.employeeForeclosureReportFile.payday})
            case FileType.grossAdvanceReportFile:
                return t(`grossAdvanceReportFileDescriptionTemplate`, {ns: 'files', period: `${file.grossAdvanceReportFile.year}${String(file.grossAdvanceReportFile.month).padStart(2, '0')}`})
            case FileType.manualSendBackPaysReportFile:
                return t(`manualSendBackPaysReportFileDescriptionTemplate`, {ns: 'files', payday: file.manualSendBackPaysReportFile.payday})
        }
    }

    const getFilename = (file: FileModel) => {
        let filename = ''

        if (file.type === FileType.advancePayoutBasisFile) {
            filename = file.advancePayoutBasisFile.filename
        } else if (file.type === FileType.deductionPayoutBasisFile) {
            filename = file.deductionPayoutBasisFile.filename
        } else if (file.type === FileType.payDeductionNotificationFile) {
            filename = file.employerPayDeductionPaymentNotificationFile.filename
        } else if (file.type === FileType.bankAccountFile) {
            filename = file.bankAccountFile.filename
        } else if (file.type === FileType.employeeDeactivationFile) {
            filename = file.employeeDeactivationFile.filename
        } else if (file.type === FileType.creditReportFile) {
            filename = file.creditReportFile.filename
        } else if (file.type === FileType.agiAdjustmentSieFile) {
            return file.agiAdjustmentSieFile.filename
        } else if (file.type === FileType.agiAdjustmentReportFile) {
            return file.agiAdjustmentReportFile.filename
        } else if (file.type === FileType.foraAdjustmentReportFile) {
            return file.foraAdjustmentReportFile.filename
        } else if (file.type === FileType.remainingDebtReportFile) {
            return file.remainingDebtReportFile.filename
        } else if (file.type === FileType.employeeForeclosureReportFile) {
            return file.employeeForeclosureReportFile.filename
        } else if (file.type === FileType.grossAdvanceReportFile) {
            return file.grossAdvanceReportFile.filename
        } else if (file.type === FileType.manualSendBackPaysReportFile) {
            return file.manualSendBackPaysReportFile.filename
        }

        let extension = filename.substring(filename.indexOf('.'))
        return getFileTitle(file).replaceAll(' ', '_') + extension
    }

    const padWithLeadingZeroes = (num: number, size: number) => {
        let numberString = num?.toString() ?? '';
        while (numberString.length < size) {
            numberString = "0" + numberString;
        }
        return numberString;
    }

    return (
        <>
        { renderFile() }
        </>
    )
}

export default File

const useStyles = createUseStyles({
    fileContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 8,
        width: '100%'
    },
    readFile: {
        '&:hover': {
            color: Colors.euro_800,
        }
    },
    newFile: {
        fontFamily: Fonts.semiBold,
        '&:hover': {
            color: Colors.euro_800,
        }
    }
})
